import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { formatRub } from "../../../lib/format";
import {
  BOT_LAUNCH_MODE_LABELS,
  RESOURCE_STATUS_LABELS,
  RESOURCE_TYPE_LABELS,
  TARGET_ACTION_LABELS,
  labelFrom,
} from "../../../lib/labels";
import { withInteractiveReadTimeout } from "../../../lib/requestTimeout";
import { sponsorApi } from "../lib/api";

type Resource = {
  id: number;
  name: string;
  type: string;
  status: string;
  url: string;
  target_action: string;
  bot_launch_mode?: "start" | "unique_start" | null;
  verification_mode: string;
  price_per_action: string;
  daily_limit: number | null;
  total_limit: number | null;
  daily_conversions?: number;
  conversions_total?: number;
  even_distribution: boolean;
  hold_enabled: boolean;
  system_bot_verified_at: string | null;
  botstat_verified_at: string | null;
};

type Props = {
  resourceId: number;
  onBack: () => void;
};

export function ResourceDetails({ resourceId, onBack }: Props) {
  const [resource, setResource] = useState<Resource | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [dailyLimit, setDailyLimit] = useState("");
  const [totalLimit, setTotalLimit] = useState("");
  const [retryGeneration, setRetryGeneration] = useState(0);

  useEffect(() => {
    let active = true;
    setResource(null);
    setError(null);
    void withInteractiveReadTimeout((signal) =>
      sponsorApi.getResource(resourceId, signal),
    )
      .then((r) => {
        if (!active) return;
        if (r.data) {
          const next = r.data as Resource;
          setResource(next);
          setDailyLimit(next.daily_limit?.toString() ?? "");
          setTotalLimit(next.total_limit?.toString() ?? "");
        } else {
          setError("не удалось открыть ресурс");
        }
      })
      .catch(() => {
        if (active) setError("не удалось открыть ресурс");
      });
    return () => {
      active = false;
    };
  }, [resourceId, retryGeneration]);

  async function saveSettings(patch: Record<string, unknown>) {
    if (!resource || saving) return;
    setSaving(true);
    setSettingsError(null);
    try {
      const r = await sponsorApi.patchResourceSettings(resource.id, patch);
      if (r.error || !r.data) {
        const detail = (
          r.error as { detail?: string; error?: { message?: string } }
        )?.detail;
        setSettingsError(detail ?? "не удалось сохранить настройки");
        return;
      }
      const next = r.data as Resource;
      setResource(next);
      setDailyLimit(next.daily_limit?.toString() ?? "");
      setTotalLimit(next.total_limit?.toString() ?? "");
    } catch {
      setSettingsError("не удалось сохранить настройки");
    } finally {
      setSaving(false);
    }
  }

  if (error) {
    return (
      <div className="tp-screen">
        <div className="tp-alert">{error}</div>
        <button
          className="tp-button-secondary"
          onClick={() => setRetryGeneration((value) => value + 1)}
        >
          повторить
        </button>
        <button
          className="tp-button-primary"
          onClick={onBack}
          aria-label="назад"
        >
          ←
        </button>
      </div>
    );
  }

  if (!resource) {
    return <div className="tp-screen">загрузка...</div>;
  }

  const holdAvailable =
    (isChannelOrChat(resource.type) || resource.type === "bot") &&
    resource.verification_mode === "verified";
  const settingsAvailable = ["approved", "active", "paused"].includes(
    resource.status,
  );
  const rows = [
    ["тип ресурса", labelFrom(RESOURCE_TYPE_LABELS, resource.type)],
    ["статус", labelFrom(RESOURCE_STATUS_LABELS, resource.status)],
    ["ссылка", resource.url || "-"],
    [
      "целевое действие",
      resource.type === "bot"
        ? labelFrom(BOT_LAUNCH_MODE_LABELS, resource.bot_launch_mode ?? "start")
        : labelFrom(TARGET_ACTION_LABELS, resource.target_action),
    ],
    ["стоимость подписчика", `${formatRub(resource.price_per_action)} ₽`],
  ];
  const resourceEnabled =
    resource.status === "active" || resource.status === "approved";
  const holdLabel =
    resource.type === "bot" ? "повторно проверять запуск" : "учитывать отписки";
  const holdDescription =
    resource.type === "bot"
      ? "если выключено, запуск засчитывается без повторной проверки перед сменой ссылки"
      : "если выключено, подписка засчитывается без повторной проверки перед сменой ссылки";

  return (
    <div className="tp-screen tp-resource-settings-screen">
      <div className="tp-resource-settings-header">
        <button
          type="button"
          className="tp-resource-settings-back"
          aria-label="назад"
          onClick={onBack}
        >
          <ArrowLeft size={20} strokeWidth={2.2} />
        </button>
        <h1 className="tp-resource-settings-page-title">
          настройки {resource.name}
        </h1>
        <span aria-hidden="true" />
      </div>

      <div className="tp-resource-settings-card tp-resource-settings-info">
        {rows.map(([label, value]) => (
          <div key={label} className="tp-resource-settings-info-row">
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>

      {settingsAvailable ? (
        <div className="tp-resource-settings-card">
          {settingsError ? (
            <div className="tp-alert">{settingsError}</div>
          ) : null}
          <label className="tp-resource-settings-toggle">
            <span>включить ресурс</span>
            <input
              type="checkbox"
              className="tp-resource-settings-toggle-input"
              checked={resourceEnabled}
              disabled={saving}
              onChange={(event) =>
                saveSettings({ enabled: event.target.checked })
              }
            />
            <span
              className="tp-resource-settings-toggle-track"
              aria-hidden="true"
            >
              <span />
            </span>
          </label>
        </div>
      ) : (
        <div className="tp-card-soft text-sm text-muted-foreground">
          настройки станут доступны после одобрения ресурса.
        </div>
      )}

      {settingsAvailable ? (
        <div className="tp-resource-settings-card tp-resource-settings-advanced">
          <h2>продвинутые настройки</h2>

          {holdAvailable ? (
            <div className="tp-resource-settings-control">
              <label className="tp-resource-settings-toggle">
                <span>{holdLabel}</span>
                <input
                  type="checkbox"
                  className="tp-resource-settings-toggle-input"
                  checked={resource.hold_enabled}
                  disabled={saving || !holdAvailable}
                  onChange={(event) =>
                    saveSettings({ hold_enabled: event.target.checked })
                  }
                />
                <span
                  className="tp-resource-settings-toggle-track"
                  aria-hidden="true"
                >
                  <span />
                </span>
              </label>
              <p>{holdDescription}</p>
            </div>
          ) : null}

          <div className="tp-resource-settings-control">
            <label className="tp-resource-settings-field">
              <span>дневной лимит</span>
              <input
                className="tp-input"
                value={dailyLimit}
                inputMode="numeric"
                onChange={(event) => setDailyLimit(event.target.value)}
              />
            </label>
            <p>
              ограничение оплаченных целевых действий за сутки; использовано:{" "}
              {resource.daily_conversions ?? 0}
            </p>
          </div>

          <div className="tp-resource-settings-control">
            <label className="tp-resource-settings-field">
              <span>общий лимит</span>
              <input
                className="tp-input"
                value={totalLimit}
                inputMode="numeric"
                onChange={(event) => setTotalLimit(event.target.value)}
              />
            </label>
            <p>
              ограничение оплаченных целевых действий за всё время;
              использовано: {resource.conversions_total ?? 0}
            </p>
          </div>

          <div className="tp-resource-settings-control">
            <label className="tp-resource-settings-toggle">
              <span>распределение в течение дня</span>
              <input
                type="checkbox"
                className="tp-resource-settings-toggle-input"
                checked={
                  Boolean(resource.daily_limit) && resource.even_distribution
                }
                disabled={saving || !resource.daily_limit}
                onChange={(event) =>
                  saveSettings({ even_distribution: event.target.checked })
                }
              />
              <span
                className="tp-resource-settings-toggle-track"
                aria-hidden="true"
              >
                <span />
              </span>
            </label>
            <p>
              {resource.daily_limit
                ? "делит суточный лимит на 24 часовых слота, чтобы трафик приходил плавнее"
                : "сначала укажите и сохраните суточный лимит; без него распределение включить нельзя"}
            </p>
          </div>

          <button
            className="tp-button-primary w-full"
            disabled={saving}
            onClick={() =>
              saveSettings({
                daily_limit: dailyLimit.trim() ? Number(dailyLimit) : null,
                total_limit: totalLimit.trim() ? Number(totalLimit) : null,
                ...(dailyLimit.trim() ? {} : { even_distribution: false }),
              })
            }
          >
            {saving ? "сохраняем..." : "сохранить лимиты"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function isChannelOrChat(type: string): boolean {
  return type.startsWith("channel_") || type.startsWith("chat_");
}
